import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { BroadcastCall, BroadcastCallService, broadcastCallStatusView } from 'src/app/shared/services/broadcast-call.service';
import { Call4uAudio } from 'src/app/models/Call4uAudio';
import { Call4uAudioService } from 'src/app/shared/services/call4u-audio.service';
import {
  PartialSendConfirmService,
  scheduleErrorMessage,
} from 'src/app/shared/components/partial-send-confirm-modal/partial-send-confirm.service';
import { toastOwnError } from 'src/app/shared/interceptors/http-error.interceptor';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { backendToBrasiliaIso } from 'src/app/shared/utils/brasilia-date';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';
import { DEFAULT_SEND_WINDOWS, SendWindow, minutesOf, spMinutesOfDay } from 'src/app/models/SendWindow';
import { SendWindowsService } from 'src/app/shared/services/send-windows.service';
import {
  BROADCAST_CALL_ATTEMPTS_MAX,
  BROADCAST_CALL_ATTEMPTS_MIN,
  BROADCAST_CALL_INTERVAL_MAX,
  BROADCAST_CALL_INTERVAL_MIN,
  BROADCAST_CALL_NAME_MAX,
  BROADCAST_CALL_NAME_MIN,
} from '../broadcast-call/broadcast-call.component';

interface BroadcastUpdatePayload {
  name: string;
  call4u_audio_id: string;
  max_attempts: number;
  retry_interval_minutes: number;
  scheduled_date: string | null;
  scheduled_end_date: string | null;
  postback_url: string | null;
}

/**
 * Validator pra URL de postback. Aceita vazio (null/'') e qualquer URL
 * http(s) bem-formada; rejeita schemes desconhecidos. Roda 1x por mudança
 * de valor, não em todo CD tick (o método isPostbackUrlValid antigo
 * instanciava `new URL()` em cada chamada do template).
 */
function postbackUrlValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '').toString().trim();
  if (value === '') { return null; }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return { invalidUrl: true };
    }
    return null;
  } catch {
    return { invalidUrl: true };
  }
}

@Component({
  selector: 'app-edit-broadcast-call',
  templateUrl: './edit-broadcast-call.component.html',
  styleUrls: ['./edit-broadcast-call.component.scss'],
})
export class EditBroadcastCallComponent implements OnInit, OnDestroy {

  readonly nameMin = BROADCAST_CALL_NAME_MIN;
  readonly nameMax = BROADCAST_CALL_NAME_MAX;
  readonly attemptsMin = BROADCAST_CALL_ATTEMPTS_MIN;
  readonly attemptsMax = BROADCAST_CALL_ATTEMPTS_MAX;
  readonly intervalMin = BROADCAST_CALL_INTERVAL_MIN;
  readonly intervalMax = BROADCAST_CALL_INTERVAL_MAX;
  readonly attemptsOptions: number[] =
    Array.from({ length: BROADCAST_CALL_ATTEMPTS_MAX - BROADCAST_CALL_ATTEMPTS_MIN + 1 }, (_, i) => BROADCAST_CALL_ATTEMPTS_MIN + i);

  broadcastId!: string;
  broadcast!: BroadcastCall;
  audios: Call4uAudio[] = [];
  audioOptions: SearchSelectOption[] = [];
  loadingAudios = true;
  isSaving = false;
  loading = true;
  canEdit = false;
  subscriptions: Subscription[] = [];
  /** O PUT de salvar, com a confirmação de saldo e o reenvio: um por vez, cancelado ao sair. */
  private saveSubscription: Subscription | null = null;

  /**
   * Janela de ligação (Brasília), vinda de `GET /send-windows` — nunca hardcodada no
   * template (achado de QA, 14/10/2026): o texto do aviso e os limites de hora do
   * `app-window-datetime-picker` têm que acompanhar o que o backend configurar.
   * `null` até a resposta chegar (ou se falhar); aí quem decide de verdade é o backend.
   */
  windowCall: SendWindow | null = null;

  /** "Enviar agora" × "Agendar": fora do `FormGroup`, igual ao wizard de criar (não é um CVA). */
  sendNow = true;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(BROADCAST_CALL_NAME_MIN), Validators.maxLength(BROADCAST_CALL_NAME_MAX)]],
    audioId: ['', Validators.required],
    maxAttempts: [1, [Validators.required, Validators.min(BROADCAST_CALL_ATTEMPTS_MIN), Validators.max(BROADCAST_CALL_ATTEMPTS_MAX)]],
    retryIntervalMinutes: [30, [Validators.required, Validators.min(BROADCAST_CALL_INTERVAL_MIN), Validators.max(BROADCAST_CALL_INTERVAL_MAX)]],
    scheduledDate: [null as Date | null],
    scheduledEndDate: [null as Date | null],
    postbackUrl: ['', postbackUrlValidator],
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private broadcastCallService: BroadcastCallService,
    private call4uAudioService: Call4uAudioService,
    private toastService: ToastService,
    private partialSendConfirm: PartialSendConfirmService,
    private sendWindowService: SendWindowsService,
  ) { }

  ngOnInit(): void {
    this.broadcastId = this.route.snapshot.paramMap.get('id') ?? '';

    this.subscriptions.push(
      this.call4uAudioService.list(1, 100).subscribe({
        next: result => {
          this.loadingAudios = false;
          this.audios = result?.data || [];
          this.audioOptions = this.audios.map(audio => ({
            value: audio.id,
            label: audio.title,
            hint: this.audioHint(audio),
          }));
        },
        error: () => { this.loadingAudios = false; },
      })
    );

    this.subscriptions.push(
      this.sendWindowService.get().subscribe({
        next: windows => { this.windowCall = windows?.call || null; },
        error: () => { this.windowCall = null; },
      })
    );

    this.loadBroadcast();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.saveSubscription?.unsubscribe();
  }

  /** Janela usada pelos componentes de agendamento: nunca `null` (sem janela conhecida
   * ainda, entra o padrão só para os limites do campo de hora — quem trava de verdade é o backend). */
  get effectiveWindowCall(): SendWindow {
    return this.windowCall || DEFAULT_SEND_WINDOWS.call;
  }

  get statusLabel(): string {
    return broadcastCallStatusView(this.broadcast).label;
  }

  /** Converte a cor do status para o tom do `m8v-status-pill` (mesmo mapeamento do detalhe). */
  get statusTone(): string {
    const cls = broadcastCallStatusView(this.broadcast).cls;
    switch (cls) {
      case 'warning': return 'warn';
      case 'info': return 'cool';
      case 'success': return 'ok';
      case 'danger': return 'danger';
      default: return 'muted';
    }
  }

  brasiliaIso(value: string | null | undefined): string | null {
    return backendToBrasiliaIso(value);
  }

  loadBroadcast(): void {
    this.loading = true;
    this.subscriptions.push(
      this.broadcastCallService.getById(this.broadcastId).pipe(
        finalize(() => this.loading = false)
      ).subscribe({
        next: data => {
          this.broadcast = data.broadcast;
          this.canEdit = !['Concluído', 'Cancelado'].includes(this.broadcast.status?.status);
          this.prefillForm();
          if (!this.canEdit) {
            this.form.disable();
          }
        },
        error: () => {
          this.toastService.error('Erro ao carregar broadcast de voz');
        },
      })
    );
  }

  prefillForm(): void {
    const scheduledDate = this.parseBackendDate(this.broadcast.scheduled_date);
    const scheduledEndDate = this.parseBackendDate(this.broadcast.scheduled_end_date);
    // Sem `scheduled_date`: a campanha foi criada para sair imediatamente — mesmo
    // critério do wizard (`sendNow` só existe no front, o backend não grava esse flag).
    this.sendNow = !scheduledDate;

    this.form.patchValue({
      name: this.broadcast.name,
      audioId: this.broadcast.call4u_audio_id,
      maxAttempts: this.broadcast.max_attempts,
      retryIntervalMinutes: this.broadcast.retry_interval_minutes,
      scheduledDate,
      scheduledEndDate,
      postbackUrl: this.broadcast.postback_url || '',
    });
    this.syncScheduleControls();
  }

  /**
   * String "naive" do backend (UTC sem offset) -> `Date` absoluto, pronto para o CVA de
   * `app-window-datetime-picker` (que extrai o relógio de parede de Brasília via `spParts`).
   * Não é o helper antigo de `datetime-local` (removido): isto só interpreta o fuso de
   * leitura, não grava nada.
   */
  private parseBackendDate(value: string | null | undefined): Date | null {
    const iso = this.brasiliaIso(value);
    if (!iso) { return null; }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  get postbackUrlControl(): AbstractControl | null {
    return this.form.get('postbackUrl');
  }

  setSendNow(value: boolean): void {
    this.sendNow = value;
    this.syncScheduleControls();
  }

  /**
   * Em "Enviar agora" as datas não vão no PUT (`submit` manda `null`), então os pickers
   * ficam desabilitados e fora da validade do formulário: uma hora bloqueada digitada
   * antes de trocar para "Enviar agora" não pode travar o Salvar sem nada na tela.
   */
  private syncScheduleControls(): void {
    if (!this.canEdit) { return; } // Concluído/Cancelado: o formulário inteiro fica desabilitado.
    for (const name of ['scheduledDate', 'scheduledEndDate']) {
      const control = this.form.get(name)!;
      if (this.sendNow) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  formatDuration(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    const parts: string[] = [];
    if (min > 0) { parts.push(`${min}min`); }
    parts.push(`${sec}s`);
    return parts.join(' ');
  }

  private audioHint(audio: Call4uAudio): string | undefined {
    const parts: string[] = [];
    if (audio.duration_seconds) { parts.push(this.formatDuration(audio.duration_seconds)); }
    if (audio.status && audio.status !== 'approved') { parts.push('não aprovado'); }
    return parts.length ? parts.join(' · ') : undefined;
  }

  /**
   * Review do MR !558 (ação 11): com "Agendar" marcado, o `submit()` mandava qualquer data —
   * inclusive nenhuma (vira `scheduled_date: null`, que o backend lê como "enviar agora") ou
   * uma fora da janela de ligação (o picker só bloqueia ESCOLHER um horário novo fora dela; um
   * valor já gravado que caiu fora — janela reconfigurada, por exemplo — continua no controle,
   * de propósito, para a UI mostrar "bloqueado"). Mesmas regras do passo 4 do wizard de criar
   * (`BroadcastCallComponent.stepErrors(4)`): início preenchido e, se houver final, final
   * depois do início. Fim da janela EXCLUSIVO (>=) — `CallSendWindow::isBlocked()` no backend
   * usa `[start, stop)`, diferente da janela de SMS (que é inclusiva nos dois lados); bloquear
   * aqui o que o backend também bloqueia evita um 400 no PUT. "Início no futuro" fica com o
   * backend na edição: uma campanha já em andamento (só Concluído/Cancelado bloqueiam a edição)
   * tem início no passado e precisa poder ser renomeada. `scheduled_end_date` não é conferido
   * contra a janela (gap conhecido do backend, fora deste escopo).
   */
  scheduleError(): string | null {
    if (this.sendNow) { return null; }

    const start = this.form.get('scheduledDate')!.value as Date | null;
    const end = this.form.get('scheduledEndDate')!.value as Date | null;

    if (!start || isNaN(start.getTime())) {
      return 'Preencha a data e a hora de início do agendamento.';
    }
    if (this.windowCall) {
      const minute = spMinutesOfDay(start);
      if (minute < minutesOf(this.windowCall.start) || minute >= minutesOf(this.windowCall.stop)) {
        return `O início precisa estar dentro da janela de ligação (${this.windowCall.start}–${this.windowCall.stop}, horário de Brasília).`;
      }
    }
    if (end && end.getTime() <= start.getTime()) {
      return 'A data final precisa ser depois da inicial.';
    }
    return null;
  }

  submit(): void {
    if (this.isSaving || !this.canEdit || this.form.invalid) { return; }

    const scheduleError = this.scheduleError();
    if (scheduleError) {
      this.toastService.error(scheduleError);
      return;
    }

    this.isSaving = true;

    const v = this.form.value;
    const trimmedPostback = (v.postbackUrl || '').trim();
    // Mesma regra do wizard de criar: `Date.prototype.toJSON` (app.module.ts) já serializa
    // a hora de parede de Brasília certa, `spWallTimeToDate` monta o `Date` no CVA — nada
    // de helper de fuso aqui, só `.toISOString()` direto.
    const data: BroadcastUpdatePayload = {
      name: v.name,
      call4u_audio_id: v.audioId,
      max_attempts: Number(v.maxAttempts),
      retry_interval_minutes: Number(v.retryIntervalMinutes),
      scheduled_date: this.sendNow ? null : ((v.scheduledDate as Date | null)?.toISOString() || null),
      scheduled_end_date: this.sendNow ? null : ((v.scheduledEndDate as Date | null)?.toISOString() || null),
      postback_url: trimmedPostback === '' ? null : trimmedPostback,
    };

    // Campanha ainda pendente com saldo abaixo do custo: o backend pede confirmação (422) e o
    // modal decide se reenvia com `confirm_partial` (T.10).
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.partialSendConfirm.schedule(
      confirmPartial => this.broadcastCallService.update(this.broadcastId, data, confirmPartial)
    ).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: () => {
        this.toastService.success('Broadcast atualizado com sucesso!');
        this.router.navigate(['/broadcasts-call']);
      },
      error: err => toastOwnError(this.toastService, scheduleErrorMessage(err, 'Erro ao atualizar broadcast de voz')),
    });
  }

  cancel(): void {
    this.router.navigate(['/broadcasts-call']);
  }
}
